import { AgentConfig } from "../types";
import { AutonomousIntelligence } from '@/services/intelligence/AutonomousIntelligence';
import { audioIntelligence } from '@/services/audio/AudioIntelligenceService';
import systemPrompt from '@agents/brand/prompt.md?raw';
import { buildDomainRetrievalTools, buildDomainRetrievalDeclarations } from '../tools/DomainTools';
import { BrandTools } from '../tools/BrandTools';

const brandRetrievalConfig = {
    brand_guidelines: {
        path: 'brand_guidelines',
        requiresUserIdFilter: true,
        description: 'Brand bibles, tone of voice guidelines, and visual identity pillars.',
        defaultLimit: 5
    },
    visual_assets: {
        path: 'visual_assets',
        requiresUserIdFilter: true,
        description: 'Logos, color palettes, typography, and approved visual assets.',
        defaultLimit: 10
    },
    moodboards: {
        path: 'moodboards',
        requiresUserIdFilter: true,
        description: 'Creative moodboards and thematic inspirations.',
        defaultLimit: 5
    }
};

const brandRetrievalTools = buildDomainRetrievalTools('Brand', brandRetrievalConfig);
const brandRetrievalDeclarations = buildDomainRetrievalDeclarations('Brand', brandRetrievalConfig);

export const BrandAgent: AgentConfig = {
    id: 'brand',
    name: 'Brand Director',
    description: 'Protects the integrity and consistency of the artist brand.',
    color: 'bg-slate-400',
    category: 'department',
    systemPrompt: systemPrompt,
    functions: {
        ...brandRetrievalTools,
        verify_output: async (args: { goal: string, content: string }) => {
            const prompt = `Critique the following content against the stated goal/guidelines.
            Goal: ${args.goal}
            Content: ${args.content}
            
            Provide a pass/fail assessment and specific feedback.`;
            try {
                const response = await AutonomousIntelligence.generateText(prompt, { maxOutputTokens: 8192, temperature: 1.0 });
                return { success: true, data: { critique: response } };
            } catch (e: unknown) {
                return { success: false, error: e instanceof Error ? e.message : String(e) };
            }
        },
        analyze_brand_consistency: async (args: { content?: string, type?: string, assetPath?: string, brandKit?: Record<string, unknown> }) => {
            try {
                // Asset path → deterministic compliance engine (Workstream D).
                // Absorbs the old desktop-only vision bridge; web/desktop parity.
                if (args.assetPath) {
                    const { useStore } = await import('@/core/store');
                    const { userProfile } = useStore.getState();
                    const brandKit = userProfile?.brandKit;
                    if (!brandKit) {
                        return { success: false, error: 'No Brand Kit found. Set up your Brand Kit before running a visual consistency scan.' };
                    }
                    const { scanAsset } = await import('@/services/brand/BrandComplianceService');
                    const report = await scanAsset(args.assetPath, brandKit);
                    return { success: true, data: { analysis: report } };
                }

                // Fallback to text-based analysis
                const prompt = `Analyze the following ${args.type || 'content'} for brand consistency.
                Content: ${args.content}

                Evaluate: Tone of Voice, Visual/Descriptive Alignment, and Core Values.
                Return a Score (0-100) and actionable feedback.`;
                const response = await AutonomousIntelligence.generateText(prompt, { maxOutputTokens: 8192, temperature: 1.0 });
                return { success: true, data: { analysis: response } };
            } catch (e: unknown) {
                return { success: false, error: e instanceof Error ? e.message : String(e) };
            }
        },
        scan_brand_compliance: BrandTools.scan_brand_compliance,
        generate_brand_guidelines: async (args: { name: string, values: string[] }) => {
            const prompt = `Generate a comprehensive Brand Bible for "${args.name}".
            Core Values: ${args.values.join(', ')}
            
            Include:
            1. Mission Statement
            2. Tone of Voice
            3. Visual Identity Pillars
            4. Do's and Don'ts`;
            try {
                const response = await AutonomousIntelligence.generateText(prompt, { maxOutputTokens: 8192, temperature: 1.0 });
                return { success: true, data: { guidelines: response } };
            } catch (e: unknown) {
                return { success: false, error: e instanceof Error ? e.message : String(e) };
            }
        },
        audit_visual_assets: async (args: { assets: string[] }) => {
            const results = [];
            for (const assetUrl of args.assets) {
                try {
                    const prompt = `Critique this visual asset against standard brand guidelines (Logo usage, Color palette, Typography). Provide a pass/fail score (0-100) and specific feedback.`;
                    const analysis = await AutonomousIntelligence.analyzeImage(prompt, assetUrl);
                    results.push({ asset: assetUrl, analysis });
                } catch (e: unknown) {
                    results.push({ asset: assetUrl, error: (e as Error).message });
                }
            }
            return {
                success: true,
                data: {
                    message: "Visual audit complete.",
                    results
                }
            };
        },
        analyze_audio: async (args: { uploadedAudioIndex: number }) => {
            const { useStore } = await importWithRetry(() => import('@/core/store'));
            const { uploadedAudio } = useStore.getState();
            const audioItem = uploadedAudio[args.uploadedAudioIndex || 0];

            if (!audioItem) {
                return { success: false, error: "No audio found. Please upload audio first." };
            }

            try {
                const fetchRes = await fetch(audioItem.url);
                const blob = await fetchRes.blob();
                const file = new File([blob], "audio_track.mp3", { type: blob.type });

                const profile = await audioIntelligence.analyze(file);
                return { success: true, data: { analysis: profile } };
            } catch (e: unknown) {
                return { success: false, error: e instanceof Error ? e.message : String(e) };
            }
        },
        analyze_brand_sentiment: async (args: { text: string; context?: string }) => {
            const prompt = `Analyze the brand sentiment of the following text.
            Context: ${args.context || 'General public discussion'}
            Text: ${args.text}
            
            Evaluate the overall sentiment, dominant emotion, key themes, and provide a summary of public perception.`;
            try {
                const response = await AutonomousIntelligence.generateText(prompt, { maxOutputTokens: 8192, temperature: 1.0 });
                return { success: true, data: { analysis: response } };
            } catch (e: unknown) {
                return { success: false, error: e instanceof Error ? e.message : String(e) };
            }
        },
        update_brand_color: async (args: { from: string; to: string }) => {
            const { updateBrandColorByName } = await importWithRetry(() => import('@/services/brand/updateBrandColor'));
            const result = await updateBrandColorByName(args.from, args.to);
            if (!result.success) {
                return { success: false, error: result.message };
            }
            return { success: true, data: { matchedColor: result.matchedColor, newColor: result.newColor, message: result.message } };
        },
        generate_brand_kit: async (args: { description: string; core_values: string[] }) => {
            const prompt = `Generate a comprehensive brand kit based on the following description and core values.
            Description: ${args.description}
            Core Values: ${args.core_values.join(', ')}
            
            Include suggested color hex codes, typography/font pairings, a description of the brand voice, and a logo concept.`;
            try {
                const response = await AutonomousIntelligence.generateText(prompt, { maxOutputTokens: 8192, temperature: 1.0 });
                return { success: true, data: { brandKit: response } };
            } catch (e: unknown) {
                return { success: false, error: e instanceof Error ? e.message : String(e) };
            }
        },
        fetch_brand_kit: McpTools.fetch_brand_kit
    },
    authorizedTools: ['verify_output', 'analyze_brand_consistency', 'scan_brand_compliance', 'generate_brand_guidelines', 'audit_visual_assets', 'analyze_audio', 'analyze_brand_sentiment', 'update_brand_color', 'generate_brand_kit', 'list_domain_records', 'fetch_brand_kit'],
    tools: [{
        functionDeclarations: [
            ...brandRetrievalDeclarations,
            {
                name: 'verify_output',
                description: 'Critique and verify generated content against a goal (Brand Bible).',
                parameters: {
                    type: 'OBJECT',
                    properties: {
                        goal: { type: 'STRING', description: 'The original goal or brand guidelines.' },
                        content: { type: 'STRING', description: 'The content to verify.' }
                    },
                    required: ['goal', 'content']
                }
            },
            {
                name: 'analyze_brand_consistency',
                description: 'Analyze content for tone, core values, and visual consistency.',
                parameters: {
                    type: 'OBJECT',
                    properties: {
                        content: { type: 'STRING', description: 'The text or asset description to analyze.' },
                        type: { type: 'STRING', description: 'Type of content (e.g., "social post", "email", "image").' },
                        assetPath: { type: 'STRING', description: 'Optional: Local path to an image or video asset for vision analysis.' },
                        brandKit: { type: 'OBJECT', description: 'Optional: Specific brand guidelines to use for analysis (colors, fonts, vibe).' }
                    },
                    required: ["content", "type"]
                }
            },
            {
                name: 'scan_brand_compliance',
                description: 'Scan a generated or uploaded visual asset against the user\'s Brand Kit (palette colors, fonts, logo/safe-zone, aesthetic identity) and return a structured compliance report with a 0-100 score. Failing assets need an explicit override reason before delivery.',
                parameters: {
                    type: 'OBJECT',
                    properties: {
                        assetIndex: { type: 'NUMBER', description: 'Index of the asset across the user\'s generated history and uploads.' },
                        assetId: { type: 'STRING', description: 'Optional: exact history/upload asset id (takes precedence over assetIndex).' }
                    }
                }
            },
            {
                name: 'generate_brand_guidelines',
                description: 'Generate structured brand guidelines based on core values.',
                parameters: {
                    type: 'OBJECT',
                    properties: {
                        name: { type: 'STRING', description: 'Name of the brand.' },
                        values: { type: 'ARRAY', description: 'List of core values.', items: { type: 'STRING' } }
                    },
                    required: ['name', 'values']
                }
            },
            {
                name: 'audit_visual_assets',
                description: 'Audit a list of visual assets for compliance.',
                parameters: {
                    type: 'OBJECT',
                    properties: {
                        assets: { type: 'ARRAY', description: 'List of asset URLs or names to audit.', items: { type: 'STRING' } }
                    },
                    required: ['assets']
                }
            },
            {
                name: 'analyze_audio',
                description: 'Analyze an uploaded audio track for BPM, Key, Genre, and Vibe.',
                parameters: {
                    type: 'OBJECT',
                    properties: {
                        uploadedAudioIndex: { type: 'NUMBER', description: 'Index of the audio file in the upload list (default 0).' }
                    },
                    required: []
                }
            },
            {
                name: 'analyze_brand_sentiment',
                description: 'Analyze public sentiment or text for brand perception, dominant emotions, and key themes.',
                parameters: {
                    type: 'OBJECT',
                    properties: {
                        text: { type: 'STRING', description: 'The text, comments, or articles to analyze.' },
                        context: { type: 'STRING', description: 'Optional context regarding the source of the text.' }
                    },
                    required: ['text']
                }
            },
            {
                name: 'update_brand_color',
                description: 'Rename a color in the artist\'s saved Brand Kit palette by matching its current name (e.g. change "Golden Hour Amber" to "Silver Hour Amber"). Fuzzy-matches the existing color name; fails with the available color list if no unambiguous match is found.',
                parameters: {
                    type: 'OBJECT',
                    properties: {
                        from: { type: 'STRING', description: 'The current name of the color to rename, as the artist refers to it.' },
                        to: { type: 'STRING', description: 'The new name for that color.' }
                    },
                    required: ['from', 'to']
                }
            },
            {
                name: 'generate_brand_kit',
                description: 'Generate a complete brand kit including colors, typography, and voice based on a description.',
                parameters: {
                    type: 'OBJECT',
                    properties: {
                        description: { type: 'STRING', description: 'A description of the brand identity or vibe.' },
                        core_values: { type: 'ARRAY', description: 'List of core values.', items: { type: 'STRING' } }
                    },
                    required: ['description', 'core_values']
                }
            },
            {
                name: "fetch_brand_kit",
                description: "Fetch the artist's brand kit from the remote MCP backend.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        artistId: { type: "STRING" }
                    },
                    required: ["artistId"]
                }
            }
        ]
    }]
};

import { freezeAgentConfig } from '../FreezeDiagnostic';
import { importWithRetry } from '@/utils/dynamicImport';
import { McpTools } from '../tools/McpTools';

// Freeze the schema to prevent cross-test contamination
freezeAgentConfig(BrandAgent);
